// Today's Activity — itemized, retailer-wise log of everything the caller
// did today: visits, orders, and activity-form submissions. Deliberately
// built on the EXISTING list endpoints (/visits, /orders, /activity-
// submissions, /attendance/today) rather than a new aggregator — each
// already supports date_from/date_to + self-scoping via VisibilityResolver,
// so there's no new backend surface here, just composing what's there.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ChevronRight, Clock, CheckCircle2, XCircle, ShoppingCart, FileText, Loader2, Phone,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { FormDetailDialog, OrderDetailDialog, VisitDetailDialog } from "@/components/activity/ActivityDetailSheets";

function todayIso() {
  // Same IST-shift convention used elsewhere (RetailerDashboard.jsx,
  // TadaScreen.jsx) — the app's "today" is IST, not the browser's local day.
  return new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
}

function timeOf(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }); }
  catch { return null; }
}

export default function TodayActivity() {
  const nav = useNavigate();
  const { user } = useAuth();
  // A distributor doesn't punch attendance — the "Not punched in yet
  // today" banner is meaningless noise for them, not an actual gap.
  const showAttendance = user?.role_code !== "DISTRIBUTOR";
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState(null);
  const [rows, setRows] = useState([]); // one row per retailer touched today
  const [reasonLabels, setReasonLabels] = useState(new Map()); // np reason code -> label

  useEffect(() => {
    const date = todayIso();
    setLoading(true);
    // `user_id` is explicit on every list call, not left to default
    // visibility scoping — a manager (e.g. ASM, who also punches visits
    // themselves via the Joint Call shortcut) has team-wide visibility by
    // default, which would otherwise pull the whole team's activity onto
    // what's meant to be a personal "my today" page.
    Promise.all([
      api.get("/attendance/today").catch(() => ({ data: null })),
      api.get("/visits", { params: { user_id: user.id, date_from: date, date_to: date, page_size: 100 } }),
      api.get("/orders", { params: { user_id: user.id, date_from: date, date_to: date, page_size: 100, enrich: "grid" } }),
      api.get("/activity-submissions", { params: { user_id: user.id, date_from: date, date_to: date, page_size: 100 } }),
      // Order lines only snapshot `skus.name`, which is null for every
      // bulk-imported SKU (see bulk_io/specs/skus.py — no `name` column at
      // all). The real display name — product name + pack variant — is only
      // composed server-side by the /skus list endpoint. Fetch the whole
      // (small, ~75-row) catalog once and build that same name ourselves,
      // rather than showing the raw sku_code on every line.
      api.get("/skus", { params: { page_size: 1000 } }).catch(() => ({ data: { items: [] } })),
      // The non-productive badge only showed the raw outcome ("non_productive"),
      // not why — resolve the visit_np_reason code on each visit to its label.
      api.get("/lookups", { params: { category: "visit_np_reason", page_size: 100 } })
         .catch(() => ({ data: { items: [] } })),
    ]).then(async ([att, visits, orders, forms, skus, npReasons]) => {
      setAttendance(att.data || null);

      const skuNameById = new Map(
        (skus.data.items || []).map((s) => [s.id, s.name || s.sku_code])
      );
      const npReasonLabelByCode = new Map(
        (npReasons.data.items || []).map((r) => [r.code, r.label])
      );
      setReasonLabels(npReasonLabelByCode);

      // SKU-wise breakdown per line lives on the order detail endpoint, not
      // the list one (list only has page-level rollups via enrich=grid).
      // A "today" list is short, so N+1 detail fetches here is fine.
      const orderItems = orders.data.items || [];
      const details = await Promise.all(
        orderItems.map((o) => api.get(`/orders/${o.id}`).then((r) => r.data).catch(() => o))
      );
      for (const o of details) {
        for (const l of o.lines || []) {
          l.display_name = skuNameById.get(l.sku_id) || l.sku_name_snapshot || l.sku_code_snapshot;
        }
      }

      // Group everything by retailer_id so the page reads "retailer-wise",
      // as asked — a retailer can show up via a visit, an order, a form
      // submission, or any combination of the three today.
      const byRetailer = new Map();
      const bucket = (id, name, code) => {
        if (!byRetailer.has(id)) {
          byRetailer.set(id, { id, name, code, visit: null, orders: [], forms: [] });
        }
        return byRetailer.get(id);
      };

      for (const v of visits.data.items || []) {
        const b = bucket(v.retailer_id, v.retailer_name, v.retailer_code);
        // A retailer can be visited more than once today (reopen) — keep
        // the most recent by check_in_ts (list is already sorted desc).
        if (!b.visit) b.visit = v;
      }
      for (const o of details) {
        // Primary orders (distributor → company) have no retailer_id at
        // all by design — bucketing one under a null key crashed every
        // render on this page (`row.id.slice(...)` on a null id) the day
        // any primary order landed alongside today's secondary activity.
        if (!o.retailer_id) continue;
        bucket(o.retailer_id, o.retailer_name, o.retailer_code).orders.push(o);
      }
      for (const f of forms.data.items || []) {
        if (!f.retailer_id) continue; // some forms aren't retailer-scoped
        bucket(f.retailer_id, f.retailer_name, f.retailer_code).forms.push(f);
      }

      // Sort by most recent activity — the LATEST of every timestamp touching
      // this retailer today (check-in, check-out, any order, any form), not
      // just check-in. A long visit that started early but only just ended
      // is more "recent" than a short visit that started later and already
      // finished — check-in alone put it in start-time order instead.
      const latestTs = (r) => {
        const stamps = [
          r.visit?.check_in_ts, r.visit?.check_out_ts,
          ...r.orders.map((o) => o.booking_ts),
          ...r.forms.map((f) => f.submission_ts),
        ].filter(Boolean);
        return stamps.length ? stamps.reduce((max, t) => (t > max ? t : max)) : "";
      };
      const list = Array.from(byRetailer.values())
        .sort((a, b) => latestTs(b).localeCompare(latestTs(a)));
      setRows(list);
    }).catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [user.id]);

  const totals = rows.reduce((acc, r) => {
    // "Calls" counts finished visits only — one still checked-in
    // (status "in_progress") isn't done yet, so it's excluded here even
    // though it still shows in the list below with its own badge.
    acc.visits += r.visit && r.visit.status !== "in_progress" ? 1 : 0;
    acc.orders += r.orders.length;
    for (const o of r.orders) {
      for (const l of o.lines || []) acc.kg += Number(l.qty_kg) || 0;
    }
    return acc;
  }, { visits: 0, orders: 0, kg: 0 });

  return (
    <div className="p-4 pb-24 space-y-3" data-testid="today-activity-screen">
      <button onClick={() => nav(-1)} className="text-sm text-neutral-500 flex items-center gap-1"
              data-testid="today-activity-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-400">
          {todayIso()}
        </div>
        <h1 className="text-xl font-extrabold text-neutral-900">Today's Activity</h1>
      </div>

      {/* Attendance strip */}
      {showAttendance && (
        <Card className="border-neutral-200 rounded-2xl" data-testid="ta-attendance">
          <CardContent className="p-3 flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-neutral-500 shrink-0" />
            {attendance?.check_in_ts ? (
              <span className="text-neutral-700">
                Punched in {timeOf(attendance.check_in_ts)}
                {attendance.check_out_ts && ` · out ${timeOf(attendance.check_out_ts)}`}
              </span>
            ) : (
              <span className="text-amber-700">Not punched in yet today.</span>
            )}
          </CardContent>
        </Card>
      )}

      {/* Day totals */}
      <div className="grid grid-cols-3 gap-2">
        <StatTile label="Calls" value={totals.visits} />
        <StatTile label="Orders" value={totals.orders} />
        <StatTile label="KG" value={totals.kg.toFixed(1)} />
      </div>

      {loading && (
        <div className="py-10 text-center text-neutral-400">
          <Loader2 className="h-5 w-5 inline animate-spin" />
        </div>
      )}

      {!loading && rows.length === 0 && (
        <Card className="border-dashed border-neutral-300 rounded-2xl" data-testid="ta-empty">
          <CardContent className="p-6 text-center text-sm text-neutral-500">
            Nothing logged yet today — visits, orders and form
            submissions will show up here as you go.
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {rows.map((r) => (
          <RetailerActivityCard key={r.id} row={r} reasonLabels={reasonLabels} />
        ))}
      </div>
    </div>
  );
}

function StatTile({ label, value, small }) {
  return (
    <Card className="border-neutral-200 rounded-xl">
      <CardContent className="p-2 text-center">
        <div className={(small ? "text-sm" : "text-lg") + " font-extrabold text-neutral-900"}>
          {value}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      </CardContent>
    </Card>
  );
}

function RetailerActivityCard({ row, reasonLabels }) {
  const v = row.visit;
  // Non-productive badge used to show just the raw outcome string
  // ("non_productive") with no explanation. Append the reason — preferring
  // the human label from the lookup, falling back to the raw code if the
  // lookup fetch failed or the code is stale/unrecognized.
  const npReason = v?.non_productive_reason;
  const npReasonLabel = npReason ? (reasonLabels.get(npReason) || npReason) : null;
  const [openOrder, setOpenOrder] = useState(null);
  const [openForm, setOpenForm] = useState(null);
  const [openVisit, setOpenVisit] = useState(false);
  return (
    <Card className="border-neutral-200 rounded-2xl" data-testid={`ta-row-${(row.id || "unknown").slice(0, 8)}`}>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] text-neutral-400">{row.code}</div>
            <div className="text-sm font-semibold text-neutral-900 truncate">{row.name}</div>
          </div>
          {v && (
            <span className={"text-[10px] font-semibold rounded-full px-2 py-0.5 shrink-0 text-right "
              + (v.status === "completed"
                  ? (v.outcome === "productive" ? "bg-emerald-100 text-emerald-800" : "bg-neutral-200 text-neutral-700")
                  : "bg-amber-100 text-amber-800")}>
              {v.status === "in_progress"
                ? "In progress"
                : v.outcome === "non_productive"
                  ? <>Non-productive{npReasonLabel && <><br />{npReasonLabel}</>}</>
                  : (v.outcome || v.status)}
            </span>
          )}
        </div>

        {v && (
          <button type="button" onClick={() => setOpenVisit(true)} data-testid="ta-visit-tap"
                  className="w-full flex items-center gap-1.5 text-[11px] text-neutral-500 text-left">
            <Phone className="h-3 w-3" />
            <span className="flex-1">
              Visited {timeOf(v.check_in_ts)}
              {v.check_out_ts && ` – ${timeOf(v.check_out_ts)}`}
            </span>
            <span className="text-[#6D2C91] font-semibold">Details</span>
            <ChevronRight className="h-3 w-3 text-neutral-400" />
          </button>
        )}

        {row.orders.map((o) => (
          <div key={o.id} role="button" tabIndex={0} onClick={() => setOpenOrder(o)}
               onKeyDown={(e) => e.key === "Enter" && setOpenOrder(o)} data-testid={`ta-order-tap-${o.order_no}`}
               className="bg-neutral-50 rounded-lg px-2 py-1.5 space-y-1 cursor-pointer active:bg-neutral-100">
            <div className="flex items-center gap-1.5 text-xs text-neutral-700">
              <ShoppingCart className="h-3 w-3 shrink-0 text-[#E5197F]" />
              <span className="font-mono flex-1">{o.order_no}</span>
              <span className="text-[#6D2C91] font-semibold text-[11px]">Details</span>
              <ChevronRight className="h-3 w-3 text-neutral-400" />
            </div>
            {/* SKU-wise kg — order value deliberately not shown here. */}
            {(o.lines || []).map((l) => (
              <div key={l.id}
                   className="flex items-center justify-between text-[11px] text-neutral-600 pl-4">
                <span className="truncate">{l.display_name}</span>
                <span className="font-mono shrink-0 ml-2">
                  {l.qty_kg != null
                    ? `${Number(l.qty_kg).toFixed(2)} kg`
                    : `${l.qty_pcs ?? l.entered_qty ?? "—"} pcs`}
                </span>
              </div>
            ))}
          </div>
        ))}

        {row.forms.map((f) => (
          <button type="button" key={f.id} onClick={() => setOpenForm(f)} data-testid={`ta-form-tap-${f.id.slice(0, 8)}`}
                  className="w-full flex items-center gap-1.5 text-xs text-neutral-600 text-left">
            <FileText className="h-3 w-3 shrink-0 text-[#6D2C91]" />
            <span className="truncate flex-1">{f.form_name_snapshot || "Activity form"}</span>
            {f.status === "verified"
              ? <CheckCircle2 className="h-3 w-3 text-emerald-600 shrink-0" />
              : f.status === "rejected"
                ? <XCircle className="h-3 w-3 text-red-500 shrink-0" />
                : null}
            <span className="text-[#6D2C91] font-semibold text-[11px]">Details</span>
            <ChevronRight className="h-3 w-3 text-neutral-400" />
          </button>
        ))}

        <OrderDetailDialog order={openOrder} onClose={() => setOpenOrder(null)} />
        <FormDetailDialog submissionId={openForm?.id} title={openForm?.form_name_snapshot} onClose={() => setOpenForm(null)} />
        <VisitDetailDialog visit={openVisit ? v : null} retailerName={row.name} reasonLabel={npReasonLabel}
                           orderNos={row.orders.map((o) => o.order_no)} onClose={() => setOpenVisit(false)} />
      </CardContent>
    </Card>
  );
}
